import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'react-hot-toast'
import { Building, Users, CreditCard, Save, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

const organizationSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  slug: z.string().min(3, 'Slug must be at least 3 characters').regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  industry: z.string(),
  company_size: z.string(),
  billing_email: z.string().email('Invalid email address')
})

type OrganizationFormData = z.infer<typeof organizationSchema>

export function OrganizationSettings() {
  const { organization, profile } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const canEditOrg = profile?.role === 'owner' || profile?.role === 'admin'
  
  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: organization?.name || '',
      slug: organization?.slug || '',
      website: organization?.website || '',
      industry: organization?.industry || '',
      company_size: organization?.company_size || '',
      billing_email: organization?.billing_email || profile?.email || ''
    }
  })
  
  const onSubmit = async (data: OrganizationFormData) => {
    if (!canEditOrg) {
      toast.error('You do not have permission to edit organization settings')
      return
    }
    
    setIsLoading(true)
    try {
      // Here would be the API call to update organization
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      
      toast.success('Organization settings updated')
    } catch (error) {
      toast.error('Failed to update organization settings')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Organization Settings
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage your organization information and preferences
        </p>
      </div>
      
      {!canEditOrg && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You need admin or owner permissions to modify organization settings.
            Contact your organization administrator to make changes.
          </AlertDescription>
        </Alert>
      )}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Basic Information
              </CardTitle>
              <CardDescription>
                Basic details about your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canEditOrg} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Slug</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canEditOrg} />
                    </FormControl>
                    <FormDescription>
                      Used in URLs and API endpoints. Only lowercase letters, numbers, and hyphens.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} type="url" placeholder="https://example.com" disabled={!canEditOrg} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          {/* Company Details */}
          <Card>
            <CardHeader>
              <CardTitle>Company Details</CardTitle>
              <CardDescription>
                Additional information about your company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        disabled={!canEditOrg}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="">Select Industry</option>
                        <option value="financial-services">Financial Services</option>
                        <option value="real-estate">Real Estate</option>
                        <option value="insurance">Insurance</option>
                        <option value="lending">Lending</option>
                        <option value="e-commerce">E-commerce</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="technology">Technology</option>
                        <option value="other">Other</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Size</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        disabled={!canEditOrg}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="">Select Size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1000 employees</option>
                        <option value="1000+">1000+ employees</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          {/* Subscription Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>
                Current subscription and billing information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Current Plan</Label>
                  <p className="text-sm text-muted-foreground">
                    {organization?.subscription_tier || 'Free'} Plan
                  </p>
                </div>
                <Badge variant={organization?.subscription_status === 'active' ? 'default' : 'secondary'}>
                  {organization?.subscription_status || 'inactive'}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Monthly Lead Limit</Label>
                  <p className="text-2xl font-bold">{organization?.monthly_lead_limit?.toLocaleString() || '1,000'}</p>
                </div>
                <div>
                  <Label className="text-sm">Leads Used This Month</Label>
                  <p className="text-2xl font-bold">{organization?.monthly_leads_checked?.toLocaleString() || '0'}</p>
                </div>
              </div>
              
              <FormField
                control={form.control}
                name="billing_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" disabled={!canEditOrg} />
                    </FormControl>
                    <FormDescription>
                      Email address where billing notifications will be sent
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          {/* Team Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Summary
              </CardTitle>
              <CardDescription>
                Current team members and roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Members:</span>
                  <span className="font-medium">5</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Admins:</span>
                  <span className="font-medium">2</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Analysts:</span>
                  <span className="font-medium">3</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" disabled={!canEditOrg}>
                Manage Team Members
              </Button>
            </CardContent>
          </Card>
          
          {/* Actions */}
          {canEditOrg && (
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset()}
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isDirty || isLoading}
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />}
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}